import React, { Component, ReactNode, ErrorInfo } from 'react';
import { ErrorPage } from '../ErrorDisplay/ErrorDisplay';
import { ErrorCategory, ErrorSeverity, createError, logError } from '@services/errorHandling';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Error Boundary to catch React component errors
 *
 * Wraps the application to catch unhandled errors in components,
 * preventing the entire app from crashing.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log the error with structured logging
        const appError = createError(
            ErrorCategory.UI,
            ErrorSeverity.CRITICAL,
            'React Error Boundary caught an error',
            error,
            {
                componentStack: errorInfo.componentStack,
                errorBoundary: true,
            }
        );
        logError(appError);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        window.location.href = '/';  // Hard reset to home
    };

    render() {
        if (this.state.hasError && this.state.error) {
            return (
                <ErrorPage
                    error={this.state.error}
                    onHome={this.handleReset}
                />
            );
        }

        return this.props.children;
    }
}
